import FormImageCropper from 'components/form/FormImageCropper';
import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';

import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import type { AcctStateType } from 'stores/atoms/acctState';
import { getCompanyImageUrl, getDivSelectOptions } from 'utils/app';
import type {
  SettingCompanyFormStateType,
  UseSettingCompanyResult,
} from '../hooks/useSettingCompany';

import type { JSX } from 'react';

type Props = {
  register: UseFormRegister<SettingCompanyFormStateType>;
  errors: FieldErrors<SettingCompanyFormStateType>;
  isAdmin: boolean;
  imageCropper: UseSettingCompanyResult['imageCropper'];
  activePlan: UseSettingCompanyResult['activePlan'];
  acctState: AcctStateType;
  onImageUpload: () => void;
  onImageDelete: () => void;
};

/**
 * カンパニー設定 - 会社情報セクション
 */
export default function CompanyBasicInfoSection({
  register,
  errors,
  isAdmin,
  imageCropper,
  activePlan,
  acctState,
  onImageUpload,
  onImageDelete,
}: Props): JSX.Element {
  return (
    <>
      <div className="form-about-us">
        <div className="d-flex justify-content-start align-items-center">
          <div className="flex-shrink-0 flex-img me-4">
            <FormImageCropper
              isOpen={imageCropper.isOpen}
              setIsOpen={imageCropper.setIsOpen}
              isReset={imageCropper.isReset}
              setIsReset={imageCropper.setIsReset}
              imageUrl={getCompanyImageUrl(acctState.company?.imageKey ?? null)}
              imageUrlDefault={getCompanyImageUrl(null)}
              cropWidth={256}
              cropRatio={1}
              displayWidth={80}
              setImageBlob={imageCropper.setImageBlob}
            />
          </div>
          {isAdmin && (
            <div className="flex-grow-1 flex-text mb-0 ps-1">
              <a onClick={onImageUpload} className="mb-2">
                画像アップロード
              </a>
              <br />
              <a onClick={onImageDelete}>削除</a>
            </div>
          )}
        </div>
      </div>
      <div className="form-group">
        <div className="row form-row flex-md-row justify-content-between">
          <div className="column col-lg-6 col-xl-6 col-left">
            <FormText
              subject="法人名"
              name="name"
              placeholder="株式会社サンプル"
              register={register}
              errors={errors}
              required={true}
              maxLength={100}
              requiredMark={true}
              readOnly={!isAdmin}
            />
          </div>
          <div className="column col-lg-6 col-xl-6 col-right">
            <FormText
              subject="電話番号"
              name="tel"
              placeholder="1234567890"
              register={register}
              errors={errors}
              required={true}
              maxLength={50}
              format="numeric"
              requiredMark={true}
              readOnly={!isAdmin}
            />
          </div>
        </div>
        <div className="row form-row flex-md-row justify-content-between">
          <div className="column col-lg-6 col-xl-6 col-left">
            <FormText
              subject="郵便番号"
              name="zipCode"
              placeholder="1000001"
              register={register}
              errors={errors}
              required={true}
              maxLength={7}
              minLength={7}
              format="numeric"
              requiredMark={true}
              readOnly={!isAdmin}
            />
          </div>
          <div className="column col-lg-6 col-xl-6 col-right">
            <FormSelect
              subject="都道府県"
              name="prefectureDiv"
              register={register}
              errors={errors}
              options={getDivSelectOptions('prefecture')}
              required={true}
              blank={true}
              blankLabel="選択してください"
              requiredMark={true}
              disabled={!isAdmin}
            />
          </div>
        </div>
        <div className="row form-row flex-md-row justify-content-between">
          <div className="column col-lg-6 col-xl-6 col-left">
            <FormText
              subject="市区町村"
              name="city"
              placeholder="千代田区"
              register={register}
              errors={errors}
              required={true}
              maxLength={100}
              requiredMark={true}
              readOnly={!isAdmin}
            />
          </div>
          <div className="column col-lg-6 col-xl-6 col-right">
            <FormText
              subject="番地以降"
              name="address"
              placeholder="丸の内1-1-1"
              register={register}
              errors={errors}
              required={true}
              maxLength={100}
              requiredMark={true}
              readOnly={!isAdmin}
            />
          </div>
        </div>
        <div className="row form-row flex-md-row justify-content-between">
          <div className="column col-lg-6 col-xl-6 col-left">
            <FormText
              subject="建物名・部屋番号"
              name="buildingName"
              placeholder="サンプルビル1F"
              register={register}
              errors={errors}
              maxLength={100}
              requiredMark={true}
              readOnly={!isAdmin}
            />
          </div>
          <div className="column col-lg-6 col-xl-6 col-right"></div>
        </div>
        <div className="row form-row flex-md-row justify-content-between">
          <div className="column col-lg-6 col-xl-6 col-left">
            <div className="form-label">参画中プラン</div>
            <p className="ms-3 fw-bold mb-0">{activePlan?.name ?? ''}</p>
          </div>
        </div>
      </div>
    </>
  );
}
